import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, ArrowUp, ImagePlus, MessagesSquare, MoreHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, mediaUrl, timeAgo } from "@/lib/api";
import { chunkedUpload } from "@/lib/upload";
import { useAuth } from "@/context/Auth";
import { Avatar, BackButton, Empty, PageHeader, Spinner } from "@/components/Common";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SafetyReportDialog } from "@/components/SafetyReport";

const hhmm = (s: string) => new Date(s).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
export const useRooms = () => useQuery({ queryKey: ["rooms"], queryFn: () => api.get("/rooms").then((r) => r.data) });

export function RoomList({ limit }: { limit?: number }) {
  const { data, isLoading } = useRooms();
  if (isLoading) return <Spinner />;
  if (!data?.length) return <Empty testId="rooms-empty">Nessuna stanza aperta.</Empty>;
  return (
    <div className="surface p-1" data-testid="rooms-list">
      {data.slice(0, limit).map((r: any) => (
        <Link key={r.slug} to={`/stanze/${r.slug}`} className="row rounded-2xl" data-testid={`room-link-${r.slug}`}>
          <MessagesSquare size={18} className="text-[hsl(var(--primary))] shrink-0" />
          <span className="flex-1 min-w-0"><span className="font-semibold block">{r.name}</span><span className="text-xs text-ink/60 block truncate">{r.description}</span></span>
          {r.last_message_at && <span className="text-xs text-ink/50 shrink-0">{timeAgo(r.last_message_at)}</span>}
          <ArrowRight size={15} className="text-ink/40 shrink-0" />
        </Link>
      ))}
    </div>
  );
}

export default function Stanze() {
  return (
    <div data-testid="stanze-page">
      <PageHeader kicker="Community" title="Stanze" back="/cresciamo" />
      <div className="px-5 md:px-10 max-w-3xl space-y-4 pb-12">
        <p className="text-ink/70 -mt-2">Chat aperte a tutta la community, divise per argomento. Sii gentile: ogni messaggio si può segnalare e chi blocchi sparisce dalle stanze.</p>
        <RoomList />
      </div>
    </div>
  );
}

function MessageMenu({ m, mine, onRemoved }: any) {
  const qc = useQueryClient();
  const [report, setReport] = useState(false);
  const block = async () => {
    if (!window.confirm(`Bloccare ${m.sender.display_name}? Non vedrai più i suoi messaggi.`)) return;
    try { await api.post(`/users/${m.sender.id}/block`); toast.success("Utente bloccato"); qc.invalidateQueries({ queryKey: ["room"] }); onRemoved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => { try { await api.delete(`/rooms/messages/${m.id}`); onRemoved(m.id); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button aria-label="Opzioni messaggio" className="p-1 rounded-full text-ink/45 hover:text-ink" data-testid="room-msg-menu-btn"><MoreHorizontal size={16} /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="menu-panel">
          {mine ? <DropdownMenuItem onClick={remove} data-testid="room-msg-delete">Elimina messaggio</DropdownMenuItem> : (
            <>
              {m.sender?.username && <DropdownMenuItem asChild><Link to={`/u/${m.sender.username}`} data-testid="room-msg-profile">Vedi profilo</Link></DropdownMenuItem>}
              <DropdownMenuItem onClick={() => setReport(true)} data-testid="room-msg-report">Segnala messaggio</DropdownMenuItem>
              <DropdownMenuItem onClick={block} className="text-[hsl(var(--magenta))]" data-testid="room-msg-block">Blocca utente</DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <SafetyReportDialog url={`/rooms/messages/${m.id}/report`} title="Segnala messaggio" open={report} onOpenChange={setReport} />
    </>
  );
}

function Bubble({ m, mine, onRemoved }: any) {
  return (
    <div className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid="room-message">
      {!mine && <Avatar user={m.sender} size={30} />}
      <div className={`max-w-[78%] min-w-0 ${mine ? "items-end" : ""} flex flex-col`}>
        {!mine && <span className="text-xs font-semibold text-ink/70 px-1">{m.sender?.display_name}</span>}
        <div className={`rounded-2xl px-3.5 py-2 ${mine ? "bg-[hsl(var(--primary))] text-white" : "bg-white/75"}`}>
          {m.media && <img src={mediaUrl(m.media.url)} alt="Foto" className="rounded-xl mb-1 max-h-64 object-cover" data-testid="room-message-photo" />}
          {m.text && <p className="text-[15px] whitespace-pre-wrap break-words">{m.text}</p>}
        </div>
        <span className="text-[11px] text-ink/50 px-1 flex items-center gap-1">{hhmm(m.created_at)}{!m.local && <MessageMenu m={m} mine={mine} onRemoved={onRemoved} />}</span>
      </div>
    </div>
  );
}

function RoomComposer({ slug, onSent }: { slug: string; onSent: (m: any) => void }) {
  const [text, setText] = useState("");
  const [photo, setPhoto] = useState<{ preview: string; progress: number; media?: any } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const pick = async (f: File) => {
    setPhoto({ preview: URL.createObjectURL(f), progress: 0 });
    try { const media = await chunkedUpload(f, new AbortController().signal, (p) => setPhoto((x) => x && { ...x, progress: p }), undefined, "room"); setPhoto((x) => x && { ...x, media }); }
    catch (e) { toast.error(errMsg(e)); setPhoto(null); }
  };
  const ready = (text.trim() || photo?.media) && !(photo && !photo.media) && !busy;
  const send = async () => {
    if (!ready) return;
    setBusy(true);
    try { const { data } = await api.post(`/rooms/${slug}/messages`, { text, media_id: photo?.media?.id }); setText(""); setPhoto(null); onSent(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="composer" data-testid="room-composer">
      {photo && (
        <div className="relative w-20 h-20 mb-2 rounded-xl overflow-hidden" data-testid="room-composer-photo">
          <img src={photo.preview} alt="" className="w-full h-full object-cover" />
          {!photo.media && <div className="absolute inset-0 bg-black/55 text-white flex items-center justify-center text-[11px] font-mono">{photo.progress}%</div>}
          <button onClick={() => setPhoto(null)} aria-label="Rimuovi foto" className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5" data-testid="room-photo-remove"><X size={12} /></button>
        </div>
      )}
      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden" data-testid="room-file-input" onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ""; }} />
        <button onClick={() => fileRef.current?.click()} aria-label="Allega foto" className="icon-btn" data-testid="room-attach-btn"><ImagePlus size={20} /></button>
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={1} placeholder="Scrivi nella stanza…" aria-label="Messaggio" data-testid="room-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && window.matchMedia("(pointer: fine)").matches) { e.preventDefault(); send(); } }}
          className="flex-1 resize-none bg-transparent outline-none py-2.5 max-h-32 text-[15px] placeholder:text-muted-foreground" />
        <button onClick={send} disabled={!ready} aria-label="Invia" className="send-btn" data-testid="room-send-btn"><ArrowUp size={20} strokeWidth={2.4} /></button>
      </div>
    </div>
  );
}

function useRoomMessages(slug: string) {
  const [msgs, setMsgs] = useState<any[]>([]);
  const { data, isLoading, error } = useQuery({ queryKey: ["room", slug], queryFn: () => api.get(`/rooms/${slug}/messages`).then((r) => r.data) });
  useEffect(() => { if (data) setMsgs(data.messages); }, [data]);
  useEffect(() => {
    const t = setInterval(async () => {
      const last = msgs[msgs.length - 1]?.created_at;
      try {
        const { data: d } = await api.get(`/rooms/${slug}/messages`, { params: last ? { after: last, since: msgs[0].created_at } : {} });
        setMsgs((cur) => {
          const gone = new Set<string>(d.removed_ids || []);
          const kept = gone.size ? cur.filter((m) => !gone.has(m.id)) : cur;
          const ids = new Set(kept.map((m) => m.id));
          const add = d.messages.filter((m: any) => !ids.has(m.id));
          return add.length || kept.length !== cur.length ? [...kept, ...add] : cur;
        });
      } catch { /* next poll retries */ }
    }, 4000);
    return () => clearInterval(t);
  }, [slug, msgs]);
  return { room: data?.room, msgs, setMsgs, isLoading, error };
}

export function RoomPage() {
  const { slug = "" } = useParams();
  const { user } = useAuth();
  const { room, msgs, setMsgs, isLoading, error } = useRoomMessages(slug);
  const ref = useRef<HTMLDivElement>(null);
  const lastId = msgs[msgs.length - 1]?.id;
  useLayoutEffect(() => { const el = ref.current; if (el && el.scrollHeight - el.scrollTop - el.clientHeight < 200) el.scrollTop = el.scrollHeight; }, [lastId]);
  useLayoutEffect(() => { const el = ref.current; if (el) el.scrollTop = el.scrollHeight; }, [isLoading]);
  if (isLoading) return <div className="p-10"><Spinner /></div>;
  if (error || !room) return <div className="p-10"><Empty testId="room-not-found">Stanza non trovata.</Empty></div>;
  const removed = (id?: string) => setMsgs((cur) => (id ? cur.filter((m) => m.id !== id) : cur));
  return (
    <div className="flex flex-col h-[calc(100dvh-var(--top-h)-var(--nav-h))] md:h-[100dvh] max-w-3xl" data-testid="room-page">
      <div className="px-5 md:px-10 pt-4 md:pt-8 pb-3 flex items-center gap-3">
        <BackButton parent="/stanze" />
        <div className="min-w-0"><h1 className="font-semibold text-xl truncate" data-testid="room-title">{room.name}</h1><p className="text-xs text-ink/60 truncate">{room.description}</p></div>
      </div>
      <div ref={ref} className="flex-1 overflow-y-auto px-5 md:px-10 py-3 space-y-3 overscroll-contain" data-testid="room-messages">
        {!msgs.length ? <Empty testId="room-empty">Ancora nessun messaggio. Rompi il ghiaccio con un saluto.</Empty>
          : msgs.map((m) => <Bubble key={m.id} m={m} mine={m.sender?.id === user?.id} onRemoved={removed} />)}
      </div>
      <div className="px-3 md:px-10 pb-3 pt-2"><RoomComposer slug={slug} onSent={(m) => setMsgs((cur) => (cur.some((x) => x.id === m.id) ? cur : [...cur, m]))} /></div>
    </div>
  );
}
